import StockOpnamesApiV2 from 'api/v2/admins/stock-opnames-api-v2'
import RedirectButton from 'components/atoms/redirect-button'
import PaginationBar from 'components/organisms/pagination-bar/pagination-bar'
import TitlePage from 'components/atoms/title-page'
import { ALERT_TYPES } from 'constants/alert-constants'
import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router'
import { Badge, Button, ButtonGroup, Card, CardBody, Col, FormGroup, Input, Label, Row, Spinner, Table } from 'reactstrap'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const SCOPE_OPTIONS = [
  { key: 'in_progress', label: 'In Progress' },
  { key: 'all', label: 'All' }
]

const INTERNAL_CATEGORY_OPTIONS = [
  { key: '', label: 'All H/W/C' },
  { key: 'H', label: 'H - Hermes' },
  { key: 'W', label: 'W - Watch' },
  { key: 'C', label: 'C - Curated' }
]

const resolveInitialScope = () => {
  const params = new URLSearchParams(window.location.search)
  const scope = params.get('scope')

  return scope === 'all' ? 'all' : 'in_progress'
}

const resolveInitialPage = () => {
  const params = new URLSearchParams(window.location.search)
  const page = Number(params.get('page') || 1)

  return Number.isFinite(page) && page > 0 ? Math.floor(page) : 1
}

const stockOpnameListPath = ({ query, scope, internalCategoryFilter, targetDate, page }) => {
  const params = new URLSearchParams()

  if (scope === 'all') params.set('scope', 'all')
  if (query.trim()) params.set('q', query.trim())
  if (internalCategoryFilter) params.set('internal_category_filter', internalCategoryFilter)
  if (targetDate) params.set('target_date', targetDate)
  if (page > 1) params.set('page', String(page))

  const search = params.toString()
  return `/app/stock-opnames/list-page${search ? `?${search}` : ''}`
}

const progressLabel = (completed, total) => {
  const safeCompleted = Number(completed || 0)
  const safeTotal = Number(total || 0)
  const percentage = safeTotal > 0 ? Math.min(100, Math.floor((safeCompleted / safeTotal) * 100)) : 0

  return `${safeCompleted}/${safeTotal} (${percentage}%)`
}

const StockOpnameListPage = ({ pageUtils }) => {
  const history = useHistory()
  const [ stockOpnames, setStockOpnames ] = useState([])
  const [ query, setQuery ] = useState(() => new URLSearchParams(window.location.search).get('q') || '')
  const [ scope, setScope ] = useState(resolveInitialScope)
  const [ internalCategoryFilter, setInternalCategoryFilter ] = useState(() => new URLSearchParams(window.location.search).get('internal_category_filter') || '')
  const [ targetDate, setTargetDate ] = useState(() => new URLSearchParams(window.location.search).get('target_date') || '')
  const [ pagination, setPagination ] = useState({ current_page: 1, total_pages: 1, total_count: 0, per_page: 20 })
  const [ page, setPage ] = useState(resolveInitialPage)
  const [ isLoading, setIsLoading ] = useState(true)
  const latestRequestRef = useRef(0)

  const fetchStockOpnames = (params = {}) => {
    const requestId = latestRequestRef.current + 1
    latestRequestRef.current = requestId
    setIsLoading(true)

    StockOpnamesApiV2.get(params)
      .then((response) => {
        if (requestId !== latestRequestRef.current) return
        setStockOpnames(response.data.stock_opnames || [])
        setPagination(response.data.meta || { current_page: page, total_pages: 1, total_count: 0, per_page: 20 })
      })
      .catch((error) => {
        if (requestId !== latestRequestRef.current) return
        pageUtils.setApiErrorMsg(error.response?.data)
        pageUtils.setAlertMsg('Unable to fetch stock opnames', ALERT_TYPES.DANGER)
      })
      .finally(() => {
        if (requestId !== latestRequestRef.current) return
        setIsLoading(false)
      })
  }

  useEffect(() => {
    const nextUrl = stockOpnameListPath({ query, scope, internalCategoryFilter, targetDate, page })
    window.history.replaceState({}, '', nextUrl)
  }, [ query, scope, internalCategoryFilter, targetDate, page ])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchStockOpnames({
        q                        : query,
        scope,
        internal_category_filter : internalCategoryFilter,
        target_date              : targetDate,
        page,
        per_page                 : 20
      })
    }, 150)

    return () => clearTimeout(timeoutId)
  }, [ query, scope, internalCategoryFilter, targetDate, page ]) // eslint-disable-line react-hooks/exhaustive-deps

  const openStockOpname = (stockOpname) => {
    const returnTo = stockOpnameListPath({ query, scope, internalCategoryFilter, targetDate, page })
    history.push(`/app/stock-opnames/${stockOpname.id}?return_to=${encodeURIComponent(returnTo)}`)
  }

  const statusBadge = (stockOpname) => {
    return stockOpname.in_progress
      ? <Badge color='warning'>{ 'In Progress' }</Badge>
      : <Badge color='secondary'>{ 'Closed' }</Badge>
  }

  return (
    <>
      <TitlePage mainTitle={ 'Stock Opname' } subTitle={ 'List Page' } />
      <Row className='mb-3'>
        <Col md={ 8 }>
          <ButtonGroup>
            {
              SCOPE_OPTIONS.map((option) => (
                <Button
                  key={ option.key }
                  color={ scope === option.key ? 'primary' : 'secondary' }
                  outline={ scope !== option.key }
                  onClick={ () => { setScope(option.key); setPage(1) } }
                >
                  { option.label }
                </Button>
              ))
            }
          </ButtonGroup>
        </Col>
        <Col md={ 4 } className='text-right'>
          <RedirectButton
            color='primary'
            text='New Stock Opname'
            path='/app/stock-opnames/new'
          />
        </Col>
      </Row>
      <Card>
        <CardBody>
          <Row className='mb-3'>
            <Col md={ 4 }>
              <FormGroup>
                <Label>{ 'Search' }</Label>
                <Input
                  value={ query }
                  onChange={ (event) => { setQuery(event.target.value); setPage(1) } }
                  placeholder='Search stock opname title'
                />
              </FormGroup>
            </Col>
            <Col md={ 3 }>
              <FormGroup>
                <Label>{ 'Internal Category' }</Label>
                <Input
                  type='select'
                  value={ internalCategoryFilter }
                  onChange={ (event) => { setInternalCategoryFilter(event.target.value); setPage(1) } }
                >
                  {
                    INTERNAL_CATEGORY_OPTIONS.map((option) => (
                      <option key={ option.key || 'all' } value={ option.key }>{ option.label }</option>
                    ))
                  }
                </Input>
              </FormGroup>
            </Col>
            <Col md={ 3 }>
              <FormGroup>
                <Label>{ 'Target Date' }</Label>
                <Input
                  type='date'
                  value={ targetDate }
                  onChange={ (event) => { setTargetDate(event.target.value); setPage(1) } }
                />
              </FormGroup>
            </Col>
          </Row>
          {
            isLoading
              ? <Spinner color='primary' />
              : (
                <>
                  <div className='d-none d-md-block'>
                    <Table hover responsive>
                      <thead>
                        <tr>
                          <th>{ 'ID' }</th>
                          <th>{ 'Title' }</th>
                          <th>{ 'Target Date' }</th>
                          <th>{ 'Filter' }</th>
                          <th>{ 'Status' }</th>
                          <th>{ 'Total Entries' }</th>
                          <th>{ 'Total IQ' }</th>
                          <th>{ 'Cek Fisik' }</th>
                          <th>{ 'Price Tag' }</th>
                          <th>{ 'Cek Kelengkapan' }</th>
                          <th>{ 'Correction' }</th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          stockOpnames.map((stockOpname) => (
                            <tr
                              key={ stockOpname.id }
                              onClick={ () => openStockOpname(stockOpname) }
                              style={{ cursor: 'pointer' }}
                            >
                              <td>{ stockOpname.id }</td>
                              <td><strong>{ stockOpname.title }</strong></td>
                              <td>{ stockOpname.target_date || '-' }</td>
                              <td>{ stockOpname.internal_category_filter_label || '-' }</td>
                              <td>{ statusBadge(stockOpname) }</td>
                              <td>{ stockOpname.total_entries }</td>
                              <td>{ stockOpname.total_inventory_quantity || 0 }</td>
                              <td>{ progressLabel(stockOpname.total_physical_checked ?? stockOpname.total_product_found, stockOpname.total_entries) }</td>
                              <td>{ progressLabel(stockOpname.total_price_tag_exist, stockOpname.total_entries) }</td>
                              <td>{ progressLabel(stockOpname.total_all_inclusions_found, stockOpname.total_entries) }</td>
                              <td>{ stockOpname.total_needs_correction || 0 }</td>
                            </tr>
                          ))
                        }
                      </tbody>
                    </Table>
                  </div>

                  <div className='d-md-none'>
                    {
                      stockOpnames.map((stockOpname) => (
                        <Card
                          key={ stockOpname.id }
                          className='mb-2'
                          onClick={ () => openStockOpname(stockOpname) }
                          style={{ cursor: 'pointer' }}
                        >
                          <CardBody className='py-3'>
                            <div className='d-flex justify-content-between align-items-start mb-2'>
                              <div>
                                <strong>{ stockOpname.title }</strong>
                                <div className='text-muted'>{ `#${stockOpname.id}` }</div>
                              </div>
                              { statusBadge(stockOpname) }
                            </div>
                            <div>{ `Target: ${stockOpname.target_date || '-'} · Filter: ${stockOpname.internal_category_filter_label || '-'}` }</div>
                            <div>{ `Entries: ${stockOpname.total_entries} · Total IQ: ${stockOpname.total_inventory_quantity || 0}` }</div>
                            <div>{ `Cek Fisik: ${progressLabel(stockOpname.total_physical_checked ?? stockOpname.total_product_found, stockOpname.total_entries)}` }</div>
                            <div>{ `Price Tag: ${progressLabel(stockOpname.total_price_tag_exist, stockOpname.total_entries)}` }</div>
                            <div>{ `Cek Kelengkapan: ${progressLabel(stockOpname.total_all_inclusions_found, stockOpname.total_entries)}` }</div>
                            <div>{ `Correction: ${stockOpname.total_needs_correction || 0}` }</div>
                          </CardBody>
                        </Card>
                      ))
                    }
                  </div>
                </>
              )
          }

          {
            !isLoading ? (
              <div className='d-flex justify-content-between align-items-center flex-wrap mt-3'>
                <small className='text-muted mb-2'>
                  { pagination?.total_count > 0
                    ? `Showing ${((pagination.current_page - 1) * pagination.per_page) + 1}-${Math.min(pagination.current_page * pagination.per_page, pagination.total_count)} of ${pagination.total_count}`
                    : 'Showing 0 of 0' }
                </small>
                { pagination?.total_pages > 1 ? (
                  <PaginationBar
                    pagination={ pagination }
                    onClickPage={ (nextPage) => setPage(nextPage) }
                  />
                ) : null }
              </div>
            ) : null
          }
        </CardBody>
      </Card>
    </>
  )
}

StockOpnameListPage.propTypes = propTypes

export default StockOpnameListPage
